import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, ExternalLink, File, FileImage,
  FileText, ShieldCheck, Trash2, Upload,
} from 'lucide-react';
import { profileAPI } from '../../api/profile';
import { getStorageUrl } from '../../utils/storage';
import { Card } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';

/* ── constants ───────────────────────────────────────────── */
const DOC_TYPES = [
  { value: 'pan_card',    label: 'PAN Card' },
  { value: 'aadhar_card', label: 'Aadhaar Card' },
  { value: 'passport',    label: 'Passport' },
  { value: 'voter_id',    label: 'Voter ID' },
  { value: 'other',       label: 'Other' },
];

const MAX_SIZE_BYTES = 15 * 1024 * 1024; // 15 MB

const docLabel = (value) => DOC_TYPES.find((d) => d.value === value)?.label ?? value;

const isPdf = (url = '') => url.toLowerCase().endsWith('.pdf');

/* ── component ───────────────────────────────────────────── */
export default function IdProofPage() {
  const [proofs, setProofs]         = useState([]);
  const [loadingProofs, setLoadingProofs] = useState(true);

  const [docType, setDocType]       = useState('');
  const [file, setFile]             = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [success, setSuccess]       = useState(false);
  const [apiError, setApiError]     = useState('');
  const fileRef                     = useRef();

  /* API #6 — load existing id proofs ────────────────────── */
  const loadProofs = () => {
    setLoadingProofs(true);
    profileAPI
      .getProfile()
      .then((res) => setProofs(res.data.data?.id_proofs ?? []))
      .catch(() => {})
      .finally(() => setLoadingProofs(false));
  };

  useEffect(() => { loadProofs(); }, []);

  /* file picker ────────────────────────────────────────── */
  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowed.includes(selected.type)) {
      setApiError('Only JPG, PNG or PDF files are allowed.');
      return;
    }
    if (selected.size > MAX_SIZE_BYTES) {
      setApiError('File must be smaller than 5 MB.');
      return;
    }
    setApiError('');
    setFile(selected);
  };

  const clearFile = () => {
    setFile(null);
    setApiError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  /* API #9 — upload document ──────────────────────────── */
  const handleUpload = async () => {
    if (!file || !docType) {
      setApiError('Please select a document type and file.');
      return;
    }
    setApiError('');
    setUploading(true);

    const fd = new FormData();
    fd.append('document_type', docType);   // ← correct field name
    fd.append('document', file);

    try {
      await profileAPI.uploadIdProof(fd);
      clearFile();
      setDocType('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      loadProofs(); // refresh list
    } catch (err) {
      const msg =
        err.response?.data?.errors?.document?.[0] ||
        err.response?.data?.errors?.document_type?.[0] ||
        err.response?.data?.message ||
        'Upload failed. Please try again.';
      setApiError(msg);
    } finally {
      setUploading(false);
    }
  };

  /* ── render ──────────────────────────────────────────── */
  return (
    <div>
      <PageHeader title="ID Proof" subtitle="Upload your identity documents" showBack />

      <div className="px-4 py-4 pb-6 flex flex-col gap-4">

        {/* ── Uploaded documents list ─────────────────── */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} className="text-amber-500" />
            <p className="text-sm font-semibold text-gray-800">Uploaded Documents</p>
          </div>

          {loadingProofs ? (
            <div className="flex justify-center py-4">
              <div className="w-7 h-7 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
            </div>
          ) : proofs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              No documents uploaded yet.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {proofs.map((proof, idx) => {
                const url  = getStorageUrl(proof.document_url);
                const pdf  = isPdf(proof.document_url);
                return (
                  <motion.div
                    key={proof.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.06 }}
                    className="flex items-center gap-3 rounded-xl bg-amber-50 border border-amber-100 px-3 py-3"
                  >
                    <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                      {pdf
                        ? <FileText size={16} className="text-red-400" />
                        : <FileImage size={16} className="text-amber-500" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">
                        {docLabel(proof.document_type)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {pdf ? 'PDF document' : 'Image file'}
                      </p>
                    </div>
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg bg-white border border-amber-200 flex items-center justify-center hover:bg-amber-100 transition-colors"
                        title="View document"
                      >
                        <ExternalLink size={13} className="text-amber-600" />
                      </a>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </Card>

        {/* ── Upload new document ─────────────────────── */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Upload size={16} className="text-amber-500" />
            <p className="text-sm font-semibold text-gray-800">Upload New Document</p>
          </div>

          {/* Success */}
          <AnimatePresence>
            {success && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="mb-4 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2"
              >
                <CheckCircle size={16} />
                Document uploaded successfully!
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          {apiError && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600"
            >
              {apiError}
            </motion.div>
          )}

          <div className="flex flex-col gap-4">
            {/* Document type select */}
            <div className="flex flex-col gap-1">
              <label className="input-label">Document Type</label>
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="input-field"
              >
                <option value="">Select document type</option>
                {DOC_TYPES.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>

            {/* File picker */}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,.pdf"
              onChange={handleFileChange}
              className="hidden"
            />

            {file ? (
              /* File selected — show name + remove */
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
              >
                <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  {file.type === 'application/pdf'
                    ? <FileText size={16} className="text-red-400" />
                    : <FileImage size={16} className="text-amber-500" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                  <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  onClick={clearFile}
                  className="w-8 h-8 rounded-lg bg-white border border-red-100 flex items-center justify-center hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={13} className="text-red-400" />
                </button>
              </motion.div>
            ) : (
              /* Drop zone */
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-amber-200 py-7 hover:border-amber-400 hover:bg-amber-50 transition-colors"
              >
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
                >
                  <File size={20} color="white" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-700">Tap to choose document</p>
                  <p className="text-xs text-gray-400 mt-0.5">JPG, PNG or PDF · max 15 MB</p>
                </div>
              </button>
            )}

            <Button
              onClick={handleUpload}
              loading={uploading}
              fullWidth
              disabled={!file || !docType}
            >
              Upload Document
            </Button>
          </div>
        </Card>

      </div>
    </div>
  );
}
